import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from '../../../hooks/useSoundEffects';
import {
  BOAT_SHARK_CROSSING_LOGICAL_WIDTH,
  BOAT_SHARK_CROSSING_LOGICAL_HEIGHT,
  BOAT_SHARK_CROSSING_WORLD_WIDTH,
  BOAT_SHARK_CROSSING_ROCKS_SRC,
  BOAT_SHARK_CROSSING_BOAT_SRC,
  BOAT_SHARK_CROSSING_SHARK_SRC,
  BOAT_SHARK_CROSSING_DIE_SOUND_SRC,
  BOAT_SHARK_CROSSING_WIN_PICK_VALUE,
  BOAT_SHARK_CROSSING_LOSS_PICK_VALUE,
  BOAT_SHARK_CROSSING_MAX_LIVES,
  BOAT_SHARK_CROSSING_SHARK_SPAWN_INTERVAL_MS,
  BOAT_SHARK_CROSSING_SHARKS_PER_SPAWN,
  BOAT_SHARK_CROSSING_SHARK_SPEED_MIN,
  BOAT_SHARK_CROSSING_SHARK_SPEED_MAX,
  BOAT_SHARK_CROSSING_MAX_ACTIVE_SHARKS,
  BOAT_SHARK_CROSSING_PLAYER_SPEED,
  BOAT_SHARK_CROSSING_PLAY_MARGIN_Y,
  BOAT_SHARK_CROSSING_GOAL_X,
  BOAT_SHARK_CROSSING_BOAT_SPAWN,
  BOAT_SHARK_CROSSING_BOAT_WIDTH,
  BOAT_SHARK_CROSSING_BOAT_HEIGHT,
  BOAT_SHARK_CROSSING_SHARK_WIDTH,
  BOAT_SHARK_CROSSING_SHARK_HEIGHT,
  BOAT_SHARK_CROSSING_ROCKS,
  boatSharkRectsOverlap,
  boatOverlapsAnyRock,
  moveBoatWithRockBlocking,
  moveSharkWithRockBlocking,
  boatSharkRandomBetween,
  computeBoatSharkCameraX,
} from './boatSharkCrossingLayout';
import './BoatSharkCrossingMinigame.css';

const MOVE_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'a', 'A', 'd', 'D', 'w', 'W', 's', 'S']);
const COUNTDOWN_STEPS = ['3', '2', '1', 'GO'];
const COUNTDOWN_STEP_MS = 1000;
const BOAT_SHARK_CROSSING_HORN_SOUND_SRC = '/minigames/boat_horn.mp3';

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} timeMs
 */
function drawBoatSharkWater(ctx, timeMs) {
  const grad = ctx.createLinearGradient(0, 0, 0, BOAT_SHARK_CROSSING_LOGICAL_HEIGHT);
  grad.addColorStop(0, '#d4f1ff');
  grad.addColorStop(0.45, '#b8e8ff');
  grad.addColorStop(1, '#9dd9f5');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, BOAT_SHARK_CROSSING_WORLD_WIDTH, BOAT_SHARK_CROSSING_LOGICAL_HEIGHT);

  const t = timeMs / 1000;

  // Broad moving light bands (gives depth + motion)
  ctx.globalAlpha = 0.12;
  ctx.fillStyle = '#ffffff';
  for (let y = 0; y < BOAT_SHARK_CROSSING_LOGICAL_HEIGHT; y += 22) {
    const bandShift = Math.sin(t * 0.9 + y * 0.08) * 10;
    ctx.fillRect(0 + bandShift, y, BOAT_SHARK_CROSSING_WORLD_WIDTH, 6);
  }
  ctx.globalAlpha = 1;

  // Wave lines (animated sine strokes)
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)';
  for (let layer = 0; layer < 3; layer += 1) {
    const baseY = 46 + layer * 86;
    const amp = 6 + layer * 2;
    const freq = 0.018 + layer * 0.003;
    const speed = 0.9 + layer * 0.35;
    ctx.beginPath();
    for (let x = 0; x <= BOAT_SHARK_CROSSING_WORLD_WIDTH; x += 18) {
      const y = baseY + Math.sin(x * freq + t * speed) * amp;
      if (x === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();
  }

  // Foam streaks (small highlights, slow drift)
  ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
  for (let waveX = 0; waveX < BOAT_SHARK_CROSSING_WORLD_WIDTH; waveX += 56) {
    const drift = (t * 18 + waveX * 0.13) % 120;
    const waveY = 30 + Math.sin(t * 1.3 + waveX * 0.02) * 10;
    ctx.fillRect(waveX + drift, waveY, 30, 3);
    ctx.fillRect(waveX + drift + 14, BOAT_SHARK_CROSSING_LOGICAL_HEIGHT - waveY - 14, 24, 2);
  }

  // Sparkles (tiny glints; deterministic positions, time-varying intensity)
  ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
  for (let i = 0; i < 90; i += 1) {
    const x = (i * 173) % BOAT_SHARK_CROSSING_WORLD_WIDTH;
    const y = 22 + ((i * 97) % (BOAT_SHARK_CROSSING_LOGICAL_HEIGHT - 44));
    const twinkle = 0.5 + 0.5 * Math.sin(t * 2.2 + i * 0.7);
    const size = twinkle > 0.92 ? 2 : twinkle > 0.86 ? 1.5 : 0;
    if (size > 0) {
      ctx.globalAlpha = 0.18 + twinkle * 0.38;
      ctx.fillRect(x, y, size, size);
    }
  }
  ctx.globalAlpha = 1;
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {HTMLImageElement | null} rockImg
 * @param {HTMLImageElement | null} sharkImg
 * @param {HTMLImageElement | null} boatImg
 * @param {{ x: number, y: number, w: number, h: number }} boat
 * @param {import('./boatSharkCrossingLayout').BoatSharkEnemy[]} sharks
 * @param {{ x: number, y: number, size: number, life: number }[]} wake
 * @param {number} cameraX
 */
function drawBoatSharkScene(ctx, rockImg, sharkImg, boatImg, boat, sharks, wake, cameraX) {
  ctx.save();
  ctx.translate(-cameraX, 0);

  drawBoatSharkWater(ctx, performance.now());

  if (rockImg && rockImg.complete) {
    for (const rock of BOAT_SHARK_CROSSING_ROCKS) {
      ctx.drawImage(rockImg, rock.x, rock.y, rock.w, rock.h);
    }
  } else {
    ctx.fillStyle = '#64748b';
    for (const rock of BOAT_SHARK_CROSSING_ROCKS) {
      ctx.fillRect(rock.x, rock.y, rock.w, rock.h);
    }
  }

  if (sharkImg && sharkImg.complete) {
    for (const shark of sharks) {
      ctx.save();
      ctx.translate(shark.x + shark.w, shark.y);
      ctx.scale(-1, 1);
      ctx.drawImage(sharkImg, 0, 0, shark.w, shark.h);
      ctx.restore();
    }
  } else {
    ctx.fillStyle = '#475569';
    for (const shark of sharks) {
      ctx.fillRect(shark.x, shark.y, shark.w, shark.h);
    }
  }

  ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
  for (const puff of wake) {
    const alpha = Math.max(0, Math.min(1, puff.life / 900));
    if (alpha <= 0) {
      continue;
    }
    ctx.globalAlpha = alpha * 0.6;
    ctx.beginPath();
    ctx.arc(puff.x, puff.y, puff.size, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  if (boatImg && boatImg.complete) {
    ctx.save();
    ctx.translate(boat.x + boat.w, boat.y);
    ctx.scale(-1, 1);
    ctx.drawImage(boatImg, 0, 0, boat.w, boat.h);
    ctx.restore();
  } else {
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(boat.x, boat.y, boat.w, boat.h);
  }

  ctx.restore();
}

/**
 * @param {object} game
 */
function respawnBoatSharkPlayer(game) {
  Object.assign(game.boat, BOAT_SHARK_CROSSING_BOAT_SPAWN);
  game.sharks = [];
  game.wake = [];
  game.wakeSpawnMs = 0;
  game.spawnAccumulatorMs = 0;
  game.cameraX = computeBoatSharkCameraX(BOAT_SHARK_CROSSING_BOAT_SPAWN.x + BOAT_SHARK_CROSSING_BOAT_SPAWN.w / 2);
}

/**
 * @param {object} game
 * @param {(pickValue: number) => void} submitPick
 * @param {number} lossPickValue
 * @param {(nextLives: number) => void} setLivesRemaining
 * @param {(msg: number) => void} setLifeLostMessage
 * @returns {boolean} true if run ended (loss)
 */
function handleBoatSharkHit(game, submitPick, lossPickValue, setLivesRemaining, setLifeLostMessage) {
  playSoundEffect(BOAT_SHARK_CROSSING_DIE_SOUND_SRC);
  game.livesRemaining -= 1;
  const nextLives = game.livesRemaining;
  setLivesRemaining(nextLives);
  if (nextLives <= 0) {
    submitPick(lossPickValue);
    return true;
  }
  setLifeLostMessage(nextLives);
  respawnBoatSharkPlayer(game);
  return false;
}

/**
 * @param {object} game
 * @param {number} sharkSpeedMin
 * @param {number} sharkSpeedMax
 * @param {number} sharksPerSpawn
 */
function spawnBoatSharkWave(game, sharkSpeedMin, sharkSpeedMax, sharksPerSpawn) {
  const yMin = BOAT_SHARK_CROSSING_PLAY_MARGIN_Y;
  const yMax =
    BOAT_SHARK_CROSSING_LOGICAL_HEIGHT - BOAT_SHARK_CROSSING_PLAY_MARGIN_Y - BOAT_SHARK_CROSSING_SHARK_HEIGHT;
  const baseX = game.cameraX + BOAT_SHARK_CROSSING_LOGICAL_WIDTH + 12;

  for (let i = 0; i < sharksPerSpawn; i += 1) {
    if (game.sharks.length >= game.maxActiveSharks) {
      return;
    }
    const y = boatSharkRandomBetween(yMin, yMax);
    const speed = boatSharkRandomBetween(sharkSpeedMin, sharkSpeedMax);
    game.sharks.push({
      x: baseX + i * 36 + boatSharkRandomBetween(0, 24),
      y,
      w: BOAT_SHARK_CROSSING_SHARK_WIDTH,
      h: BOAT_SHARK_CROSSING_SHARK_HEIGHT,
      vx: -speed,
    });
  }
}

/**
 * @param {object} game
 * @param {number} dt
 * @param {number} boatDx
 * @param {number} boatDy
 */
function updateBoatWake(game, dt, boatDx, boatDy) {
  game.wake = Array.isArray(game.wake) ? game.wake : [];
  for (const puff of game.wake) {
    puff.life -= dt;
    puff.x += puff.vx * (dt / 16);
    puff.y += puff.vy * (dt / 16);
    puff.size += 0.06 * (dt / 16);
  }
  game.wake = game.wake.filter((puff) => puff.life > 0);

  if (Math.hypot(boatDx, boatDy) < 0.14) {
    game.wakeSpawnMs = 0;
    return;
  }
  game.wakeSpawnMs = (game.wakeSpawnMs || 0) + dt;
  while (game.wakeSpawnMs >= 42) {
    game.wakeSpawnMs -= 42;
    const boat = game.boat;
    game.wake.push({
      x: boat.x + 5 + (Math.random() * 2 - 1) * 2.5,
      y: boat.y + boat.h * 0.5 + (Math.random() * 2 - 1) * 6,
      vx: -0.85 - Math.random() * 0.6,
      vy: (Math.random() * 2 - 1) * 0.28,
      life: 540 + Math.random() * 380,
      size: 1.6 + Math.random() * 1.8,
    });
  }
}

/**
 * @param {object} props
 */
export default function BoatSharkCrossingMinigame({
  challenge,
  onPickTree,
  disabled = false,
  containerRef,
  resultOutcome = null,
  onResultProceed,
  winReward = null,
}) {
  const { t } = useTranslation();
  const arenaRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const submittedRef = useRef(false);
  const keysRef = useRef(new Set());
  const gameRef = useRef(null);
  const imagesRef = useRef({ rock: null, boat: null, shark: null });
  const countdownActiveRef = useRef(false);

  const challengeReady =
    challenge != null &&
    Number.isInteger(challenge.winPickValue) &&
    Number.isInteger(challenge.lossPickValue) &&
    Number.isInteger(challenge.sharkSpawnIntervalMs) &&
    Number.isInteger(challenge.sharksPerSpawn) &&
    typeof challenge.sharkSpeedMin === 'number' &&
    typeof challenge.sharkSpeedMax === 'number' &&
    Number.isInteger(challenge.maxActiveSharks) &&
    Number.isInteger(challenge.maxLives);

  const winPickValue = challengeReady ? challenge.winPickValue : BOAT_SHARK_CROSSING_WIN_PICK_VALUE;
  const lossPickValue = challengeReady ? challenge.lossPickValue : BOAT_SHARK_CROSSING_LOSS_PICK_VALUE;
  const sharkSpawnIntervalMs = challengeReady
    ? challenge.sharkSpawnIntervalMs
    : BOAT_SHARK_CROSSING_SHARK_SPAWN_INTERVAL_MS;
  const sharksPerSpawn = challengeReady ? challenge.sharksPerSpawn : BOAT_SHARK_CROSSING_SHARKS_PER_SPAWN;
  const sharkSpeedMin = challengeReady ? challenge.sharkSpeedMin : BOAT_SHARK_CROSSING_SHARK_SPEED_MIN;
  const sharkSpeedMax = challengeReady ? challenge.sharkSpeedMax : BOAT_SHARK_CROSSING_SHARK_SPEED_MAX;
  const maxActiveSharks = challengeReady ? challenge.maxActiveSharks : BOAT_SHARK_CROSSING_MAX_ACTIVE_SHARKS;
  const maxLives = challengeReady ? challenge.maxLives : BOAT_SHARK_CROSSING_MAX_LIVES;

  const [livesRemaining, setLivesRemaining] = useState(maxLives);
  const [lifeLostMessage, setLifeLostMessage] = useState(null);
  const [countdownLabel, setCountdownLabel] = useState(null);

  useEffect(() => {
    if (!challengeReady || resultOutcome != null) {
      countdownActiveRef.current = false;
      setCountdownLabel(null);
      return undefined;
    }

    countdownActiveRef.current = true;
    setCountdownLabel(COUNTDOWN_STEPS[0]);
    const timers = COUNTDOWN_STEPS.slice(1).map((step, index) =>
      window.setTimeout(() => {
        setCountdownLabel(step);
      }, (index + 1) * COUNTDOWN_STEP_MS)
    );
    const finishTimer = window.setTimeout(() => {
      playSoundEffect(BOAT_SHARK_CROSSING_HORN_SOUND_SRC);
      countdownActiveRef.current = false;
      setCountdownLabel(null);
    }, COUNTDOWN_STEPS.length * COUNTDOWN_STEP_MS);
    timers.push(finishTimer);

    return () => {
      for (const id of timers) {
        window.clearTimeout(id);
      }
      countdownActiveRef.current = false;
    };
  }, [challengeReady, resultOutcome, challenge]);

  useEffect(() => {
    if (lifeLostMessage == null) {
      return undefined;
    }
    const id = window.setTimeout(() => setLifeLostMessage(null), 2200);
    return () => window.clearTimeout(id);
  }, [lifeLostMessage]);

  const winRewardBodyKey =
    resultOutcome === 'won' && winReward && typeof winReward.id === 'string'
      ? `exploreUnknownPage.minigame.boatSharkCrossing.winReward.${winReward.id}`
      : null;

  const setPlayArenaRef = useCallback(
    (node) => {
      arenaRef.current = node;
      if (containerRef && typeof containerRef === 'object') {
        containerRef.current = node;
      }
    },
    [containerRef]
  );

  const submitPick = useCallback(
    (pickValue) => {
      if (submittedRef.current || disabled || resultOutcome != null) {
        return;
      }
      submittedRef.current = true;
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      onPickTree(pickValue);
    },
    [disabled, onPickTree, resultOutcome]
  );

  useEffect(() => {
    let cancelled = false;
    const load = (src) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`BoatSharkCrossingMinigame: failed to load ${src}`));
        img.src = src;
      });
    Promise.all([
      load(BOAT_SHARK_CROSSING_ROCKS_SRC),
      load(BOAT_SHARK_CROSSING_BOAT_SRC),
      load(BOAT_SHARK_CROSSING_SHARK_SRC),
    ])
      .then(([rock, boat, shark]) => {
        if (cancelled) {
          return;
        }
        imagesRef.current = { rock, boat, shark };
      })
      .catch((err) => {
        if (!cancelled) {
          throw err;
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!challengeReady || resultOutcome != null) {
      return undefined;
    }

    const resizeCanvas = () => {
      const arena = arenaRef.current;
      const canvas = canvasRef.current;
      if (!arena || !canvas) {
        return;
      }
      const w = Math.max(1, Math.floor(arena.clientWidth));
      const h = Math.max(1, Math.floor(arena.clientHeight));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
    };

    resizeCanvas();
    const arena = arenaRef.current;
    const observer = arena ? new ResizeObserver(resizeCanvas) : null;
    if (arena && observer) {
      observer.observe(arena);
    }
    submittedRef.current = false;
    setLivesRemaining(maxLives);
    setLifeLostMessage(null);
    gameRef.current = {
      boat: { ...BOAT_SHARK_CROSSING_BOAT_SPAWN },
      sharks: [],
      wake: [],
      wakeSpawnMs: 0,
      spawnAccumulatorMs: 0,
      maxActiveSharks,
      livesRemaining: maxLives,
      cameraX: computeBoatSharkCameraX(
        BOAT_SHARK_CROSSING_BOAT_SPAWN.x + BOAT_SHARK_CROSSING_BOAT_SPAWN.w / 2
      ),
    };

    const boatBounds = {
      xMin: 0,
      xMax: BOAT_SHARK_CROSSING_GOAL_X - BOAT_SHARK_CROSSING_BOAT_WIDTH,
      yMin: BOAT_SHARK_CROSSING_PLAY_MARGIN_Y,
      yMax:
        BOAT_SHARK_CROSSING_LOGICAL_HEIGHT - BOAT_SHARK_CROSSING_PLAY_MARGIN_Y - BOAT_SHARK_CROSSING_BOAT_HEIGHT,
    };

    const onKeyDown = (e) => {
      if (countdownActiveRef.current) {
        return;
      }
      if (MOVE_KEYS.has(e.key)) {
        e.preventDefault();
      }
      keysRef.current.add(e.key);
    };
    const onKeyUp = (e) => {
      keysRef.current.delete(e.key);
    };

    let lastTs = performance.now();

    const tick = (now) => {
      const canvas = canvasRef.current;
      const game = gameRef.current;
      if (!canvas || !game || submittedRef.current) {
        return;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('BoatSharkCrossingMinigame: canvas 2d context unavailable');
      }

      const dt = Math.min(48, now - lastTs);
      lastTs = now;

      const countdownActive = countdownActiveRef.current;

      if (!countdownActive) {
        const keys = keysRef.current;
        let dx = 0;
        let dy = 0;
        if (keys.has('ArrowLeft') || keys.has('a') || keys.has('A')) {
          dx -= 1;
        }
        if (keys.has('ArrowRight') || keys.has('d') || keys.has('D')) {
          dx += 1;
        }
        if (keys.has('ArrowUp') || keys.has('w') || keys.has('W')) {
          dy -= 1;
        }
        if (keys.has('ArrowDown') || keys.has('s') || keys.has('S')) {
          dy += 1;
        }
        if (dx !== 0 || dy !== 0) {
          const len = Math.hypot(dx, dy) || 1;
          dx = (dx / len) * BOAT_SHARK_CROSSING_PLAYER_SPEED * (dt / 16);
          dy = (dy / len) * BOAT_SHARK_CROSSING_PLAYER_SPEED * (dt / 16);
        }

        const boat = game.boat;
        moveBoatWithRockBlocking(boat, dx, dy, BOAT_SHARK_CROSSING_ROCKS, boatBounds);
        updateBoatWake(game, dt, dx, dy);

        game.cameraX = computeBoatSharkCameraX(boat.x + boat.w / 2);

        game.spawnAccumulatorMs += dt;
        while (game.spawnAccumulatorMs >= sharkSpawnIntervalMs) {
          game.spawnAccumulatorMs -= sharkSpawnIntervalMs;
          spawnBoatSharkWave(game, sharkSpeedMin, sharkSpeedMax, sharksPerSpawn);
        }

        const nextSharks = [];
        const cullLeft = game.cameraX - 48;
        for (const shark of game.sharks) {
          const moved = moveSharkWithRockBlocking(shark, shark.vx * (dt / 16), BOAT_SHARK_CROSSING_ROCKS);
          if (!moved) {
            continue;
          }
          if (shark.x + shark.w > cullLeft) {
            nextSharks.push(shark);
          }
        }
        game.sharks = nextSharks;

        if (boatOverlapsAnyRock(boat, BOAT_SHARK_CROSSING_ROCKS)) {
          if (
            handleBoatSharkHit(game, submitPick, lossPickValue, setLivesRemaining, setLifeLostMessage)
          ) {
            return;
          }
        } else {
          for (const shark of game.sharks) {
            if (boatSharkRectsOverlap(boat, shark)) {
              if (
                handleBoatSharkHit(game, submitPick, lossPickValue, setLivesRemaining, setLifeLostMessage)
              ) {
                return;
              }
              break;
            }
          }
        }

        if (boat.x + boat.w >= BOAT_SHARK_CROSSING_GOAL_X) {
          submitPick(winPickValue);
          return;
        }
      }

      const boat = game.boat;
      const { rock: rockImg, boat: boatImg, shark: sharkImg } = imagesRef.current;
      const scaleX = canvas.width / BOAT_SHARK_CROSSING_LOGICAL_WIDTH;
      const scaleY = canvas.height / BOAT_SHARK_CROSSING_LOGICAL_HEIGHT;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scaleX, scaleY);
      drawBoatSharkScene(ctx, rockImg, sharkImg, boatImg, boat, game.sharks, game.wake, game.cameraX);

      rafRef.current = window.requestAnimationFrame(tick);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    rafRef.current = window.requestAnimationFrame(tick);
    if (containerRef?.current && typeof containerRef.current.focus === 'function') {
      containerRef.current.focus();
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      keysRef.current.clear();
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [
    challengeReady,
    resultOutcome,
    submitPick,
    winPickValue,
    lossPickValue,
    sharkSpawnIntervalMs,
    sharksPerSpawn,
    sharkSpeedMin,
    sharkSpeedMax,
    maxActiveSharks,
    maxLives,
    containerRef,
  ]);

  const promptText =
    resultOutcome === 'won'
      ? winRewardBodyKey
        ? t(winRewardBodyKey, {
            percent: winReward?.darknessReductionApplied,
            xp: winReward?.xpGained,
            billyBucks: winReward?.billyBucksAmount,
            health: winReward?.startHealthBonus != null ? winReward.startHealthBonus : 3,
          })
        : t('exploreUnknownPage.minigame.boatSharkCrossing.success')
      : resultOutcome === 'lost'
        ? t('exploreUnknownPage.minigame.boatSharkCrossing.failure')
        : t('exploreUnknownPage.minigame.boatSharkCrossing.playPrompt');

  if (!challengeReady) {
    return (
      <div
        ref={setPlayArenaRef}
        className="boat-shark-crossing-play-arena boat-shark-crossing-play-arena--challenge-error"
        role="alert"
      >
        <p>{t('exploreUnknownPage.minigame.boatSharkCrossing.missingChallenge')}</p>
      </div>
    );
  }

  return (
    <div
      ref={setPlayArenaRef}
      className="boat-shark-crossing-play-arena"
      role="application"
      tabIndex={0}
      aria-label={t('exploreUnknownPage.minigame.boatSharkCrossing.playArenaAria')}
      onMouseDown={(e) => {
        e.currentTarget.focus();
      }}
    >
      {resultOutcome == null ? (
        <div className="boat-shark-crossing-prompt-strip">
          <p className="boat-shark-crossing-play-prompt">{t('exploreUnknownPage.minigame.boatSharkCrossing.playPrompt')}</p>
          <p className="boat-shark-crossing-lives">
            {t('exploreUnknownPage.minigame.boatSharkCrossing.livesRemaining', { count: livesRemaining })}
          </p>
          <p className="boat-shark-crossing-controls-hint">
            {t('exploreUnknownPage.minigame.boatSharkCrossing.controlsHint')}
          </p>
          {lifeLostMessage != null ? (
            <p className="boat-shark-crossing-life-lost-flash" role="status">
              {t('exploreUnknownPage.minigame.boatSharkCrossing.lifeLost', { count: lifeLostMessage })}
            </p>
          ) : null}
        </div>
      ) : null}
      <canvas ref={canvasRef} className="boat-shark-crossing-canvas" aria-hidden="true" />
      {countdownLabel != null ? (
        <div className="boat-shark-crossing-countdown-overlay" role="status" aria-live="assertive">
          <span
            key={countdownLabel}
            className={`boat-shark-crossing-countdown-text${
              countdownLabel === 'GO' ? ' boat-shark-crossing-countdown-text--go' : ''
            }`}
          >
            {countdownLabel}
          </span>
        </div>
      ) : null}
      {resultOutcome != null ? (
        <div className="boat-shark-crossing-result-overlay" role="presentation">
          <div className="boat-shark-crossing-result-banner" role="status">
            <h3 className="boat-shark-crossing-result-title">
              {resultOutcome === 'won'
                ? t('exploreUnknownPage.minigame.boatSharkCrossing.successTitle')
                : t('exploreUnknownPage.minigame.boatSharkCrossing.failureTitle')}
            </h3>
            <p className="boat-shark-crossing-result-body">{promptText}</p>
            {typeof onResultProceed === 'function' ? (
              <button
                type="button"
                className={`boat-shark-crossing-result-proceed${
                  resultOutcome === 'lost' ? ' boat-shark-crossing-result-proceed--lost' : ''
                }`}
                onClick={() => onResultProceed()}
              >
                {t('exploreUnknownPage.minigame.proceed')}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
